import { useEffect, useState } from "react";
import { LogOut, Lock, Timer } from "lucide-react";
import { useApp } from "@/context/AppContext";
import { NumPad } from "@/components/NumPad";
import { LangSwitch } from "@/components/LangSwitch";
import { SyncPill } from "@/components/SyncPill";
import OrbitScene from "@/components/OrbitScene";
import { Wordmark } from "@/components/Logo";
import { useClock } from "@/components/ClockButton";
import { openClock } from "@/lib/hr";

const ROLE_TONE = { owner: "#C7353B", manager: "#E67E22", cashier: "#2B79C2", doctor: "#1F9D55", nurse: "#16A085", receptionist: "#8E44AD" };

function StaffCard({ m, i, active, onClick, t, clocked }) {
  return (
    <button type="button" data-testid={`pin-staff-${m.name}`} onClick={onClick} style={{ animationDelay: `${i * 70}ms` }}
      className={`orbit-card-in relative flex items-center gap-2 sm:gap-3 min-h-14 py-2 ps-2 pe-3 rounded border text-start transition-colors ${active ? "border-[#F28C28] bg-[#FFF7DC] text-[#5A4300] shadow-[0_0_0_3px_rgba(233,185,73,.25)]" : "border-[#ADADAD] bg-white hover:bg-[#FFF9E6] hover:border-[#F28C28]"}`}>
      {clocked && <span className="absolute top-1 end-1 w-2.5 h-2.5 rounded-full bg-[#1F9D55] ring-2 ring-white" data-testid={`pin-clocked-${m.name}`} />}
      <span className="w-9 h-9 rounded grid place-items-center text-white font-bold text-base shrink-0" style={{ background: ROLE_TONE[m.role] || "#5F6B76" }}>{m.name.slice(0, 1).toUpperCase()}</span>
      <span className="min-w-0"><span className="block font-bold text-sm sm:text-base leading-tight break-words">{m.name}</span><span className="block text-[11px] opacity-75">{t(`role_${m.role}`)}</span></span>
    </button>
  );
}

export default function PinLock() {
  const { view, verifyPin, setStaff, logout, t, queue } = useApp();
  const clock = useClock();
  const members = (view.staff || []).filter((s) => s.active);
  const [sel, setSel] = useState(members.length === 1 ? members[0] : null);
  const [pin, setPin] = useState("");
  const [err, setErr] = useState(false);
  const [mode, setMode] = useState("unlock");

  useEffect(() => {
    if (pin.length !== 4 || !sel) return;
    verifyPin(sel, pin).then((ok) => {
      if (ok && mode === "clock") { clock(sel); setPin(""); setSel(null); }
      else if (ok) setStaff(sel);
      else { setErr(true); setPin(""); setTimeout(() => setErr(false), 700); }
    });
  }, [pin, sel, verifyPin, setStaff, mode, clock]);

  const signOut = () => {
    if (queue.length && !window.confirm(t("logout_pending_warn"))) return;
    logout();
  };

  return (
    <div data-testid="pin-lock-overlay" className="min-h-screen sky-bg text-white flex flex-col relative overflow-hidden">
      <div className="sky-stars" aria-hidden="true" /><div className="sky-stars sky-stars-2" aria-hidden="true" /><div className="sky-milky" aria-hidden="true" /><div className="sky-cloud sky-cloud-1" aria-hidden="true" /><div className="sky-cloud sky-cloud-2" aria-hidden="true" /><div className="sky-horizon" aria-hidden="true" /><div className="sky-shooter sky-shooter-1" aria-hidden="true" /><div className="sky-shooter sky-shooter-2" aria-hidden="true" /><div className="sky-shooter sky-shooter-3" aria-hidden="true" />
      <div className="relative z-10 flex items-center justify-between h-8 px-2 gap-2 bg-[#0B2A5B] border-b border-white/10 text-xs">
        <SyncPill />
        <Wordmark className="orbit-wordmark-anim hidden sm:inline-flex" size="sm" />
        <span className="flex items-center gap-2"><LangSwitch compact /></span>
      </div>
      <div className="relative z-10 flex-1 w-full max-w-6xl mx-auto px-4 py-6 grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_440px] items-center gap-4">
        <div className="hidden min-[380px]:flex justify-center h-[260px] sm:h-[360px] lg:h-[520px] items-center overflow-hidden min-w-0">
          <OrbitScene className="shrink-0 scale-[0.55] sm:scale-75 lg:scale-100" />
        </div>
        <div className="orbit-card-in rounded bg-white text-[#333333] border border-[#ADADAD] p-5 sm:p-6">
          <div className="mb-4 pb-2 border-b border-[#CCCCCC] flex items-start justify-between gap-2">
            <div>
              <h1 className="text-base font-semibold flex items-center gap-2" data-testid="pin-shop-name"><Lock className="w-4 h-4 text-[#2B79C2]" />{view.shop?.name}</h1>
              <p className="text-xs text-[#555555]">{t(mode === "clock" ? "pin_clock_hint" : "pin_who")}</p>
            </div>
            <div className="flex rounded border border-[#ADADAD] overflow-hidden text-xs font-bold shrink-0" data-testid="pin-mode">
              <button type="button" onClick={() => setMode("unlock")} data-testid="pin-mode-unlock" className={`h-8 px-3 ${mode === "unlock" ? "bg-[#F28C28] text-[#1A1A1A]" : "bg-white"}`}>{t("pin_mode_unlock")}</button>
              <button type="button" onClick={() => setMode("clock")} data-testid="pin-mode-clock" className={`h-8 px-3 flex items-center gap-1 ${mode === "clock" ? "bg-[#1F9D55] text-white" : "bg-white"}`}><Timer className="w-3.5 h-3.5" />{t("pin_mode_clock")}</button>
            </div>
          </div>
          {!members.length && <p className="text-center text-muted-foreground" data-testid="pin-no-staff">{t("pin_need_sync")}</p>}
          <div className="grid grid-cols-2 gap-2 mb-4">
            {members.map((m, i) => <StaffCard key={m.id} m={m} i={i} t={t} active={sel?.id === m.id} clocked={!!openClock(view.attendance, m.id)} onClick={() => { setSel(m); setPin(""); }} />)}
          </div>
          {sel && (
            <div className="rise" key={sel.id}>
              <div className={`flex justify-center gap-4 mb-4 ${err ? "shake" : ""}`} data-testid="pin-dots" dir="ltr">
                {[0, 1, 2, 3].map((i) => (
                  <span key={i} className={`w-4 h-4 rounded-full border-2 transition-[background-color,transform] ${err ? "border-destructive bg-destructive" : i < pin.length ? "bg-[#F28C28] border-[#F28C28] scale-125 shadow-[0_0_8px_rgba(233,185,73,.7)]" : "border-muted-foreground"}`} />
                ))}
              </div>
              {err && <p className="text-center text-destructive font-bold mb-3" data-testid="pin-error">{t("pin_wrong")}</p>}
              <NumPad value={pin} onChange={(v) => setPin(v.slice(0, 4))} testid="pin-input-digit" allowDot={false} />
            </div>
          )}
          <button type="button" onClick={signOut} data-testid="pin-signout-btn" className="mt-5 mx-auto flex items-center gap-2 text-sm text-[#555555] font-semibold hover:text-[#C7353B] transition-colors">
            <LogOut className="w-4 h-4" /> {t("sign_out_shop")}
          </button>
        </div>
      </div>
    </div>
  );
}
